import {
	Button,
	Card,
	CardBody,
	Flex,
	FlexItem,
	Grid,
	GridItem,
	Text,
	Title,
} from '@patternfly/react-core';
import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import { OutletContext } from '../../layout/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import './Category.scss';
import { CategoryQuestion, DiscoverCategory, DiscoverContext } from '../../types/discover';
import EmptyResults from '../../components/EmptyResults';
import { Question as QuestionApi, TQuestion } from '../../api/questions/Questions';
import { iconMap } from '../../helpers/global-icon-map.helper';
import { QuestionCategory } from '../../api/question-category/QuestionCategory';
import Question from './Question';
import Loader from '../../components/util/Loader';
import DatePeriodSelector from '../../components/date-period-selector/DatePeriodSelector';
import { TNewDateRange } from '../../api/types/TNewDateRange';

// ─── Synonym map (shared logic — ideally move to a shared helper file) ────────
const SYNONYM_MAP: Record<string, string[]> = {
	compensation: ['compensate', 'compensated', 'pay', 'salary', 'wage', 'remuneration'],
	compensate: ['compensation', 'pay', 'salary', 'wage'],
	pay: ['compensation', 'compensate', 'salary', 'wage'],
	salary: ['pay', 'compensation', 'wage', 'compensate'],
	hire: ['hiring', 'hired', 'recruitment', 'recruit', 'onboard'],
	hiring: ['hire', 'hired', 'recruitment', 'recruit'],
	recruitment: ['hire', 'hiring', 'recruit', 'onboard'],
	turnover: ['attrition', 'churn', 'resignation', 'resign'],
	attrition: ['turnover', 'churn', 'resignation'],
	performance: ['perform', 'review', 'appraisal', 'evaluation'],
	headcount: ['employees', 'staff', 'workforce', 'personnel', 'count'],
	employee: ['employees', 'staff', 'worker', 'personnel', 'headcount'],
	diversity: ['inclusion', 'equity', 'dei', 'belonging'],
	training: ['learning', 'development', 'upskill', 'education'],
	engagement: ['satisfaction', 'morale', 'sentiment', 'wellbeing', 'health'],
	health: ['wellbeing', 'engagement', 'wellness', 'satisfaction'],
	wellbeing: ['health', 'wellness', 'engagement', 'satisfaction'],
	absence: ['absenteeism', 'leave', 'sick', 'time off'],
	leave: ['absence', 'absenteeism', 'sick', 'time off'],
};

const expandTerms = (term: string): string[] => {
	const lower = term.toLowerCase();
	const synonyms = SYNONYM_MAP[lower] ?? [];
	return [lower, ...synonyms];
};

const matchesSearch = (target: string, expandedTerms: string[]): boolean => {
	const lower = target.toLowerCase();
	return expandedTerms.some((t) => lower.includes(t));
};

/**
 * Returns true if a question matches the expanded search terms,
 * checking name, description, and tags.
 */
const questionMatchesTerm = (q: CategoryQuestion, expandedTerms: string[]): boolean => {
	return (
		matchesSearch(q.question ?? '', expandedTerms) ||
		matchesSearch(q.description ?? '', expandedTerms) ||
		(q.tags ?? []).some((tag) => matchesSearch(tag, expandedTerms))
	);
};

type Props = {
	pageContext: DiscoverContext;
	inlineCategoryId?: number;
	showBackButton?: boolean;
	hideFilters?: boolean;
	hideDateSelector?: boolean;
	onBack?: () => void;
};

const Category = (props: Props) => {
	const { pageContext, inlineCategoryId, showBackButton, onBack, hideDateSelector } = props;
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const paramId = searchParams.get('id');
	const idFromParams = paramId ? parseInt(paramId) : undefined;
	const id = inlineCategoryId ?? idFromParams ?? undefined;
	const tag = searchParams.get('tag');
	const term = searchParams.get('term');
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [categories, setCategories] = useState<DiscoverCategory[]>([]);
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle=""
					pageDescription=""
					subNavContext={undefined}
				/>
			),
			sidebarContext: null,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded]);

	useEffect(() => {
		setIsLoading(true);
		setCategories([]);

		// ── Case 1: Direct category id lookup ────────────────────────────────
		if ((id ?? 0) > 0) {
			void QuestionCategory.Get(id!, 0, ['questions']).then((response) => {
				const newCategory: DiscoverCategory = {
					icon: iconMap[response.icon],
					id: id!,
					key: `Category_${response.title}`,
					name: response.title,
					questions: [],
				};
				setCategories([newCategory]);

				const questionResponse = response.questions as TQuestion[];
				questionResponse.forEach((question) => {
					const newCatQuestion: CategoryQuestion = {
						key: `Question_${question.name}`,
						question: question.name,
						description: question.description,
						priority: question.sequence,
						tags: [],
						hasChart: question.dashboard ? false : true,
						chartId: question.chart?.toString(),
						dashboardId: question.dashboard?.toString(),
					};
					newCategory.questions.push(newCatQuestion);
				});
				setIsLoading(false);
			});

			// ── Case 2: Tag filter ────────────────────────────────────────────────
		} else if (tag) {
			void QuestionCategory.GetPermitted().then((response) => {
				const built: DiscoverCategory[] = [];

				const promises = response.map((category) => {
					const newCategory: DiscoverCategory = {
						icon: iconMap[category.icon],
						id: category.id!,
						key: `Category_${category.title}`,
						name: category.title,
						questions: [],
					};
					built.push(newCategory);

					return QuestionApi.GetAllByCategoryId(category.id!.toString(), ['tags']).then(
						(questionResponse) => {
							questionResponse
								.filter((x) => x.category === category.id)
								.forEach((question) => {
									const newCatQuestion: CategoryQuestion = {
										key: `Question_${question.name}`,
										question: question.name,
										description: question.description,
										priority: question.sequence,
										tags: question.tags ? question.tags.map((x) => x.name) : [],
										hasChart: true,
										chartId: (question.chart ?? 0).toString(),
									};

									// Tag match: exact tag name match (case-insensitive)
									if (
										newCatQuestion.tags.some(
											(t) => t.toLowerCase() === tag.toLowerCase()
										)
									) {
										newCategory.questions.push(newCatQuestion);
									}
								});
						}
					);
				});

				void Promise.all(promises).then(() => {
					setCategories(built);
					setIsLoading(false);
				});
			});

			// ── Case 3: Free text search with synonym expansion ───────────────────
		} else if (term) {
			// Expand every word in the search term
			const words = term.trim().toLowerCase().split(/\s+/);
			const expandedTerms = Array.from(new Set(words.flatMap(expandTerms)));

			void QuestionCategory.GetPermitted().then((response) => {
				const built: DiscoverCategory[] = [];

				const promises = response.map((category) => {
					const newCategory: DiscoverCategory = {
						icon: iconMap[category.icon],
						id: category.id!,
						key: `Category_${category.title}`,
						name: category.title,
						questions: [],
					};
					built.push(newCategory);

					return QuestionApi.GetAllByCategoryId(category.id!.toString(), ['tags']).then(
						(questionResponse) => {
							questionResponse
								.filter((x) => x.category === category.id)
								.forEach((question) => {
									const newCatQuestion: CategoryQuestion = {
										key: `Question_${question.name}`,
										question: question.name,
										description: question.description,
										priority: question.sequence,
										tags: question.tags ? question.tags.map((x) => x.name) : [],
										hasChart: true,
										chartId: (question.chart ?? 0).toString(),
									};

									// Match against name, description, AND tags using synonym expansion
									if (questionMatchesTerm(newCatQuestion, expandedTerms)) {
										newCategory.questions.push(newCatQuestion);
									}
								});

							// Also include the category itself if its name matches
							// even if no individual questions matched — shows the category
							// with 0 questions so the user knows the category is relevant
						}
					);
				});

				void Promise.all(promises).then(() => {
					// Only keep categories that either name-match or have matched questions
					const filtered = built.filter(
						(c) => c.questions.length > 0 || matchesSearch(c.name, expandedTerms)
					);
					setCategories(filtered);
					setIsLoading(false);
				});
			});
		}
	}, [id, tag, term]);

	if (isLoading) {
		return (
			<Card>
				<CardBody>
					<Loader />
				</CardBody>
			</Card>
		);
	}

	if (categories && categories.length <= 0 && !isLoading) {
		return (
			<EmptyResults
				emptyStateBody="No results match the discover criteria. Head back to discover something different."
				backComponent={
					<Button
						variant="link"
						onClick={() => (onBack ? onBack() : navigate(-1))}
					>
						<Flex alignItems={{ default: 'alignItemsCenter' }}>
							<FlexItem>
								<FontAwesomeIcon icon={faArrowLeft} />
							</FlexItem>
							<FlexItem>
								<Text>Discover</Text>
							</FlexItem>
						</Flex>
					</Button>
				}
			/>
		);
	}

	const allEmpty = categories.every((c) => c.questions.length === 0);

	return (
		<Card>
			<CardBody>
				<Grid
					hasGutter
					className="categories"
				>
					{allEmpty && (
						<EmptyResults
							iconName={faInfoCircle}
							emptyStateBody="No results match the discover criteria. Head back to discover something different."
							backComponent={
								<Button
									variant="link"
									onClick={() => (onBack ? onBack() : navigate(-1))}
								>
									<Flex alignItems={{ default: 'alignItemsCenter' }}>
										<FlexItem>
											<FontAwesomeIcon icon={faArrowLeft} />
										</FlexItem>
										<FlexItem>
											<Text>Discover</Text>
										</FlexItem>
									</Flex>
								</Button>
							}
						/>
					)}

					{!isLoading &&
						categories &&
						categories.map((category) => {
							if (category.questions.length === 0) return null;

							return (
								<div key={category.key}>
									{!hideDateSelector && !props.hideFilters && (
										<Flex>
											<Text>Filters:</Text>
											<div className="filter-container">
												<div className="date-selector-container">
													<DatePeriodSelector
														applyDateRange={setSelectedDate}
													/>
												</div>
											</div>
										</Flex>
									)}
									<Grid hasGutter>
										<GridItem span={12}>
											<Flex
												alignItems={{ default: 'alignItemsCenter' }}
												className="category-title"
											>
												{showBackButton && onBack && (
													<FlexItem>
														<Button
															variant="link"
															onClick={onBack}
														>
															<FontAwesomeIcon icon={faArrowLeft} />
														</Button>
													</FlexItem>
												)}
												<FlexItem>
													<FontAwesomeIcon
														icon={category?.icon}
														size="2x"
													/>
												</FlexItem>
												<FlexItem>
													<Title
														headingLevel="h3"
														data-testid={`category-${category.key}`}
													>
														{category?.name}
													</Title>
												</FlexItem>
											</Flex>
										</GridItem>
										<Grid span={12}>
											{category?.questions.map((q: CategoryQuestion) => (
												<Question
													key={q.key}
													question={q}
													selectedDate={selectedDate}
												/>
											))}
										</Grid>
									</Grid>
								</div>
							);
						})}
				</Grid>
			</CardBody>
		</Card>
	);
};

export default Category;
